import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Star, TrendingUp, Sparkles } from "lucide-react";
import { KineticLines } from "@/components/motion";
import { CONTACT, STATS } from "@/data/site";
import { Counter } from "@/components/motion";

export default function Hero() {
  return (
    <section className="relative overflow-hidden pb-16 pt-32 lg:pb-24 lg:pt-40" data-testid="hero">
      <div className="mesh-hero pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="blob -right-10 top-24 h-96 w-96 bg-dq-orange/25" aria-hidden="true" />
      <div className="blob -left-24 bottom-0 h-80 w-80 bg-[#FFB800]/15" aria-hidden="true" />

      <div className="dq-container relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Left */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-dq-line bg-white/70 px-4 py-1.5 text-xs font-semibold text-dq-ink backdrop-blur"
              data-testid="hero-badge"
            >
              <span className="flex items-center gap-0.5 text-dq-orange">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
              </span>
              Bengaluru&rsquo;s Premium Digital Marketing Agency
            </motion.div>

            <h1 className="mt-6 font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tighter text-dq-ink sm:text-6xl lg:text-[4.2rem]">
              <KineticLines
                lines={["Transform Your", "Business Into a", "Brand Customers", "Trust."]}
                accentIndex={3}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-dq-muted"
            >
              Premium websites, Local SEO, Google Business Profile optimization, branding
              and digital marketing strategies designed to generate measurable business growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/contact" data-testid="hero-consult" className="btn-primary text-base">
                Get Free Consultation <ArrowRight className="h-5 w-5" />
              </Link>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="btn-whatsapp text-base">
                <MessageCircle className="h-5 w-5 text-[#25D366]" /> Chat on WhatsApp
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-12 grid max-w-lg grid-cols-2 gap-6 sm:grid-cols-4"
            >
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl font-extrabold text-dq-ink sm:text-3xl">
                    <Counter to={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                  </div>
                  <div className="mt-1 text-xs font-medium leading-tight text-dq-muted">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — hero visual with parallax float */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid="hero-visual"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-white dq-shadow-soft"
            >
              <img src="/generated/diziteq-dashboard.png" alt="DiziTeQ marketing dashboard showing visitors, conversions, leads and revenue analytics with growth charts"
                className="h-full w-full object-cover" width={940} height={650} />
            </motion.div>

            {/* floating glass cards */}
            <motion.div
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1, duration: 0.6 }}
              className="glass dq-shadow-soft absolute -left-4 top-10 hidden items-center gap-3 rounded-2xl px-4 py-3 sm:flex"
            >
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-dq-success/15 text-dq-success">
                <TrendingUp className="h-4.5 w-4.5" />
              </span>
              <div>
                <div className="font-display text-sm font-bold text-dq-ink">+320% Leads</div>
                <div className="text-[11px] text-dq-muted">Avg. client growth</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.6 }}
              className="glass dq-shadow-soft absolute -bottom-5 right-2 hidden items-center gap-3 rounded-2xl px-4 py-3 sm:flex"
            >
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-dq-orange/15 text-dq-orange">
                <Sparkles className="h-4.5 w-4.5" />
              </span>
              <div>
                <div className="font-display text-sm font-bold text-dq-ink">Google Certified</div>
                <div className="text-[11px] text-dq-muted">Ads & Analytics</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
